/**
 * The compliance status of one (person, course) cell, in the browser (plan
 * docs/plans/lms-plan.md 8.4; WP-31; R16, R22).
 *
 * The twin of backend/src/services/lms/compliance/status.js: the same rules in
 * the same order, the same result object. Both are held to the shared
 * fixture lib/__fixtures__/compliance-cases.json (byte-identical to
 * backend/tests/fixtures/lms/compliance-cases.json; a SHA-256 test in each
 * tree), so a rule changed here without its twin is a red test.
 *
 * Rules, in order:
 *   1. overdue       an open assignment whose due_at has passed (now > due_at)
 *   2. compliant /   a valid completion (not superseded, not expired) and no
 *      expiring      open assignment, or an open renewal not yet due;
 *                    expiring inside renewalLeadDays of its expires_at
 *   3. assigned      an open assignment not yet due
 *   4. waived        the newest assignment (by created_at) is waived
 *   5. expired       a completion exists and none is valid
 *   6. missing       a profile requires the course
 *   7. not_required  nothing applies
 *
 * Pure: never reads the clock when `now` is given, never modifies its input.
 * A key with no value is omitted from the result, never null.
 */

export const COMPLIANCE_STATUSES = [
  'compliant',
  'expiring',
  'assigned',
  'overdue',
  'expired',
  'waived',
  'missing',
  'not_required',
] as const;

export type ComplianceStatus = (typeof COMPLIANCE_STATUSES)[number];

/** The word each status is shown with (13: "each with its word"). */
export const COMPLIANCE_STATUS_WORDS: Readonly<Record<ComplianceStatus, string>> = {
  compliant: 'Compliant',
  expiring: 'Expiring',
  assigned: 'Assigned',
  overdue: 'Overdue',
  expired: 'Expired',
  waived: 'Waived',
  missing: 'Missing',
  not_required: 'Not required',
};

/** 5.2: lms_courses.renewal_lead_days DEFAULT 60. */
export const DEFAULT_RENEWAL_LEAD_DAYS = 60;

const DAY_MS = 24 * 60 * 60 * 1000;

type Instant = string | number | Date;

/** The completion columns the status reads (lms_completions, 5.5). */
export interface CompletionLike {
  id?: string | null;
  completed_at?: Instant | null;
  expires_at?: Instant | null;
  superseded_at?: Instant | null;
}

/** The assignment columns the status reads (lms_assignments, 5.4). */
export interface AssignmentLike {
  id?: string | null;
  status: string;
  source?: string | null;
  due_at?: Instant | null;
  created_at?: Instant | null;
}

export interface StatusInput {
  completions?: CompletionLike[] | null;
  assignments?: AssignmentLike[] | null;
  /** true when a profile requirement applies (for 'missing'). */
  required?: boolean;
  /** The course's renewal_lead_days; default course.renewal_lead_days, then 60. */
  renewalLeadDays?: number | null;
  course?: { renewal_lead_days?: number | null } | null;
  /** The instant to judge at; defaults to the current time. */
  now?: Instant | null;
}

export interface StatusResult {
  status: ComplianceStatus;
  due_at?: string;
  expires_at?: string;
  assignment_id?: string;
  completion_id?: string;
}

type StatusOptions = Omit<StatusInput, 'completions' | 'assignments'>;

function instantMs(value: Instant | null | undefined, what: string): number | null {
  if (value === null || value === undefined) return null;
  let ms: number;
  if (value instanceof Date) ms = value.getTime();
  else if (typeof value === 'number') ms = value;
  else if (typeof value === 'string') ms = Date.parse(value);
  else ms = Number.NaN;
  if (!Number.isFinite(ms)) throw new TypeError(`computeStatus: ${what} is not a valid instant`);
  return ms;
}

/** A completion is valid when it is not superseded and has not expired. */
export function isValidCompletion(completion: CompletionLike | null | undefined, nowMs: number): boolean {
  if (!completion) return false;
  if (completion.superseded_at !== null && completion.superseded_at !== undefined) return false;
  const expires = instantMs(completion.expires_at, 'a completion expires_at');
  return expires === null || expires > nowMs;
}

/** The valid completion that counts: the longest-lasting, then the latest completed, then the later in the list. */
export function currentValidCompletion(completions: CompletionLike[], nowMs: number): CompletionLike | null {
  let best: CompletionLike | null = null;
  let bestExpires = 0;
  let bestCompleted = 0;
  for (const c of completions) {
    if (!isValidCompletion(c, nowMs)) continue;
    const expires = instantMs(c.expires_at, 'a completion expires_at');
    const e = expires === null ? Number.POSITIVE_INFINITY : expires;
    const completed = instantMs(c.completed_at, 'a completion completed_at');
    const done = completed === null ? Number.NEGATIVE_INFINITY : completed;
    if (best === null || e > bestExpires || (e === bestExpires && done >= bestCompleted)) {
      best = c;
      bestExpires = e;
      bestCompleted = done;
    }
  }
  return best;
}

/** The latest completion by completed_at (the later one in the list on a tie). */
export function latestCompletion(completions: CompletionLike[]): CompletionLike | null {
  let best: CompletionLike | null = null;
  let bestDone = 0;
  for (const c of completions) {
    if (!c) continue;
    const completed = instantMs(c.completed_at, 'a completion completed_at');
    const done = completed === null ? Number.NEGATIVE_INFINITY : completed;
    if (best === null || done >= bestDone) {
      best = c;
      bestDone = done;
    }
  }
  return best;
}

/** The open assignment (the one due first should a list carry two). */
export function openAssignment(assignments: AssignmentLike[]): AssignmentLike | null {
  let best: AssignmentLike | null = null;
  let bestDue = 0;
  for (const a of assignments) {
    if (!a || a.status !== 'open') continue;
    const dueMs = instantMs(a.due_at, 'an assignment due_at');
    const due = dueMs === null ? Number.POSITIVE_INFINITY : dueMs;
    if (best === null || due < bestDue) {
      best = a;
      bestDue = due;
    }
  }
  return best;
}

/** The newest assignment by created_at (the later one in the list on a tie). */
export function newestAssignment(assignments: AssignmentLike[]): AssignmentLike | null {
  let best: AssignmentLike | null = null;
  let bestCreated = 0;
  for (const a of assignments) {
    if (!a) continue;
    const createdMs = instantMs(a.created_at, 'an assignment created_at');
    const created = createdMs === null ? Number.NEGATIVE_INFINITY : createdMs;
    if (best === null || created >= bestCreated) {
      best = a;
      bestCreated = created;
    }
  }
  return best;
}

function asText(value: Instant): string {
  return value instanceof Date ? value.toISOString() : String(value);
}

function withAssignment(result: StatusResult, assignment: AssignmentLike | null): StatusResult {
  if (!assignment) return result;
  if (assignment.due_at !== undefined && assignment.due_at !== null) result.due_at = asText(assignment.due_at);
  if (assignment.id !== undefined && assignment.id !== null) result.assignment_id = assignment.id;
  return result;
}

function withCompletion(result: StatusResult, completion: CompletionLike | null): StatusResult {
  if (!completion) return result;
  if (completion.expires_at !== undefined && completion.expires_at !== null) result.expires_at = asText(completion.expires_at);
  if (completion.id !== undefined && completion.id !== null) result.completion_id = completion.id;
  return result;
}

/**
 * The status of one (person, course) cell (8.4). Accepts the object form
 * computeStatus({ completions, assignments, required, renewalLeadDays, now })
 * or the positional form computeStatus(completions, assignments, options).
 */
export function computeStatus(input: StatusInput): StatusResult;
export function computeStatus(completions: CompletionLike[], assignments: AssignmentLike[], options?: StatusOptions): StatusResult;
export function computeStatus(
  input: StatusInput | CompletionLike[],
  assignmentsArg?: AssignmentLike[],
  optionsArg?: StatusOptions,
): StatusResult {
  const args: StatusInput = Array.isArray(input)
    ? { ...(optionsArg || {}), completions: input, assignments: assignmentsArg }
    : (input || {});
  const completions = args.completions === undefined || args.completions === null ? [] : args.completions;
  const assignments = args.assignments === undefined || args.assignments === null ? [] : args.assignments;
  if (!Array.isArray(completions)) throw new TypeError('computeStatus: completions must be an array');
  if (!Array.isArray(assignments)) throw new TypeError('computeStatus: assignments must be an array');
  const nowGiven = instantMs(args.now, 'now');
  const nowMs = nowGiven === null ? Date.now() : nowGiven;
  let lead: number | null | undefined = args.renewalLeadDays;
  if (lead === undefined || lead === null) lead = args.course ? args.course.renewal_lead_days : undefined;
  if (lead === undefined || lead === null) lead = DEFAULT_RENEWAL_LEAD_DAYS;
  if (typeof lead !== 'number' || !Number.isFinite(lead) || lead < 0) {
    throw new TypeError('computeStatus: renewalLeadDays must be a number of days, 0 or more');
  }
  const leadMs = lead * DAY_MS;

  const open = openAssignment(assignments);
  const valid = currentValidCompletion(completions, nowMs);

  // 1. overdue
  if (open) {
    const due = instantMs(open.due_at, 'an assignment due_at');
    if (due !== null && nowMs > due) {
      return withCompletion(withAssignment({ status: 'overdue' }, open), valid);
    }
  }

  // 2. compliant or expiring
  if (valid && (!open || open.source === 'renewal')) {
    const expires = instantMs(valid.expires_at, 'a completion expires_at');
    const expiring = expires !== null && expires - nowMs <= leadMs;
    return withAssignment(withCompletion({ status: expiring ? 'expiring' : 'compliant' }, valid), open);
  }

  // 3. assigned
  if (open) {
    return withCompletion(withAssignment({ status: 'assigned' }, open), valid);
  }

  // 4. waived
  const newest = newestAssignment(assignments);
  if (newest && newest.status === 'waived') {
    const result: StatusResult = { status: 'waived' };
    if (newest.id !== undefined && newest.id !== null) result.assignment_id = newest.id;
    return result;
  }

  // 5. expired
  if (completions.some(Boolean)) {
    return withCompletion({ status: 'expired' }, latestCompletion(completions));
  }

  // 6 and 7
  return { status: args.required === true ? 'missing' : 'not_required' };
}

export default computeStatus;
